import { BasketErrorModel, DEFAULT_ERROR_TEXT } from 'models/Errors';
import { BasketItemModel, ProductTypes, BasketPriceModel } from 'models/Basket';
import { appConfig } from 'configurations';
import { PaymentTypes } from 'models/Payment';
import { GtmCheckoutSteps, GtmEmptyEvents, externalScriptHelper, logsHelper, errorToBasketErrorModel } from 'helpers';
import { hashEmailWithCryptoJS, hashPhoneNumberWithDefaultNormalization, hashPhoneNumberWithTikTokNormalization } from 'helpers/hashHelper';

export class GoogleRepository {
    public removeDefaultProduct(item: BasketItemModel, removedQuantity: number): void {
        try {
            this.sendMessage('remove_from_cart', 'remove', this.createDefaultProductActionData(item, removedQuantity));
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:removeDefaultProduct'));
        }
    }

    public removeAdditionalProduct(
        productType: Exclude<ProductTypes, ProductTypes.DefaultProduct | ProductTypes.Sofaglide>,
        code: string,
        removedQuantity: number
    ): void {
        try {
            this.sendMessage('remove_from_cart', 'remove', this.createAdditionalProductActionData(productType, code, removedQuantity));
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:removeAdditionalProduct'));
        }
    }

    public removeSofaglideProduct(code: string, removedQuantity: number, category?: string, price?: BasketPriceModel): void {
        try {
            this.sendMessage('remove_from_cart', 'remove', this.createSofaglideProductActionData(code, removedQuantity, category, price));
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:removeSofaglideProduct'));
        }
    }

    public addDefaultProduct(item: BasketItemModel, addedQuantity: number, userId?: string): void {
        try {
            this.sendMessage('add_to_cart', this.createDefaultProductActionData(item, addedQuantity), undefined, false, this.createDefaultProductEventLevelData(item, userId));
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:addDefaultProduct'));
        }
    }

    public addAdditionalProduct(
        productType: Exclude<ProductTypes, ProductTypes.DefaultProduct | ProductTypes.Sofaglide>,
        code: string,
        addedQuantity: number
    ): void {
        try {
            this.sendMessage('add_to_cart', 'add', this.createAdditionalProductActionData(productType, code, addedQuantity));
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:addAdditionalProduct'));
        }
    }

    public addSofaglideProduct(code: string, addedQuantity: number, category?: string, price?: BasketPriceModel): void {
        try {
            this.sendMessage('add_to_cart', 'add', this.createSofaglideProductActionData(code, addedQuantity, category, price));
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:addSofaglideProduct'));
        }
    }

    private calculateCartValue(basketEntries: BasketItemModel[]): number {
        return basketEntries.reduce((total, item) => {
            return total + (item.basePrice.value * item.quantity);
        }, 0);
    }

    public checkoutStep(
        step: GtmCheckoutSteps,
        basketId: string,
        basketEntries: BasketItemModel[],
        basketLeadTime: number,
        paymentType: PaymentTypes,
        userId: string
    ): void {
        try {

            const eventActionData = {
                basketId: basketId,
                items: this.mapBasketEntitiesToAnalyticProducts(basketEntries, basketLeadTime)
            };
            const additionalParams = {
                uid: userId,
                affiliation: appConfig.gtmAffiliation,
                currency: 'GBP'
            };

            if (step === GtmCheckoutSteps.Basket) {
                const viewCartData = {
                    ...eventActionData,
                    value: this.calculateCartValue(basketEntries)
                };
                this.sendMessage('view_cart', viewCartData, additionalParams);
            }

            let eventName = this.getEventNameForCheckoutStep(step);
            const checkoutEventActionData = {
                ...eventActionData,
                checkout_step: step,
                payment_type: paymentType === PaymentTypes.None ? undefined : paymentType
            };
            this.sendMessage(eventName, checkoutEventActionData, additionalParams);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:checkoutStep'));
        }
    }
    private getEventNameForCheckoutStep(step: GtmCheckoutSteps): string {
        switch (step) {
            case GtmCheckoutSteps.Basket:
                return 'begin_checkout';
            case GtmCheckoutSteps.DeliveryDetails:
                return 'add_shipping_info';
            case GtmCheckoutSteps.DeliveryDate:
                return 'add_delivery_date';
            case GtmCheckoutSteps.PaymentOrDpn:
                return 'add_payment_info';
            case GtmCheckoutSteps.FinanceFormStepOne:
                return 'finance_step_one';
            case GtmCheckoutSteps.FinanceFormStepTwo:
                return 'finance_step_two';
            case GtmCheckoutSteps.FinanceFormStepThree:
                return 'finance_step_three';
            case GtmCheckoutSteps.FinanceFormStepFour:
                return 'finance_step_four';
            case GtmCheckoutSteps.OrderProcessing:
                return 'order_processing';
            case GtmCheckoutSteps.PaymentError:
                return 'payment_error';
            case GtmCheckoutSteps.Purchase:
                return 'payment_error';
            default:
                return 'unknown_step';
        }
    }

    public financeStep(
        step: GtmCheckoutSteps.FinanceFormStepOne | GtmCheckoutSteps.FinanceFormStepTwo | GtmCheckoutSteps.FinanceFormStepThree | GtmCheckoutSteps.FinanceFormStepFour,
        basketId: string,
        basketEntries: BasketItemModel[],
        basketLeadTime: number,
        paymentType: PaymentTypes,
        userId: string
    ): void {
        try {

            const eventActionData = {
                basketId: basketId,
                finance_step: step - GtmCheckoutSteps.FinanceFormStepOne + 1,
                payment_type: paymentType === PaymentTypes.None ? undefined : paymentType,
                items: this.mapBasketEntitiesToAnalyticProducts(basketEntries, basketLeadTime)
            };

            const additionalParams = {
                uid: userId,
                affiliation: appConfig.gtmAffiliation,
                currency: 'GBP'
            };
            this.sendMessage('start_finance_application', eventActionData, additionalParams);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:financeStep'));
        }
    }

    public financeApplicationSubmittedStep(
        basketId: string,
        basketEntries: BasketItemModel[],
        basketLeadTime: number,
        paymentType: PaymentTypes,
        userId: string,
        financeApplicationStatus: string
    ): void {
        try {
            const eventActionData = {
                basketId: basketId,
                finance_step: GtmCheckoutSteps.OrderProcessing - GtmCheckoutSteps.FinanceFormStepOne + 1,
                payment_type: paymentType === PaymentTypes.None ? undefined : paymentType,
                finance_result: financeApplicationStatus,
                items: this.mapBasketEntitiesToAnalyticProducts(basketEntries, basketLeadTime)
            };
            const additionalParams = {
                uid: userId,
                affiliation: appConfig.gtmAffiliation,
                currency: 'GBP'
            };
            this.sendMessage('finance_application_submitted', eventActionData, additionalParams);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:financeApplicationSubmittedStep'));
        }
    }

    public purchaseStep(
        orderCode: string,
        orderEntries: BasketItemModel[],
        totalPrice: number,
        tax: number,
        deliveryCost: number,
        userId: string,
        phone: string
    ): void {
        try {
            const eventActionData = {
                transaction_id: orderCode,
                value: totalPrice,
                tax: tax,
                shipping: deliveryCost,
                currency: 'GBP',
                items: this.mapBasketEntitiesToAnalyticProducts(orderEntries)
            };
            const additionalParams = {
                uid: userId,
                hashedEmail: hashEmailWithCryptoJS(userId),
                affiliation: appConfig.gtmAffiliation,
                user_hash_phone: hashPhoneNumberWithDefaultNormalization(phone),
                user_hash_phone_tt: hashPhoneNumberWithTikTokNormalization(phone),
            };
            this.sendMessage('purchase', eventActionData, additionalParams);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:purchaseStep'));
        }
    }

    public fieldEvent(fieldName: string, formName: string, basketId: string): void {
        try {
            const eventActionData = {
                form_name: formName,
                field_name: fieldName,
                app: false
            };
            const additionalParams = {
                uid: basketId,
                affiliation: appConfig.gtmAffiliation
            };
            this.sendMessage('form_field_complete', eventActionData, additionalParams);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:fieldEvent'));
        }
    }


    public sendHashedEmail(email: string, loginMethod: string): void {
        try {
            // Retrieve the dataLayer object using the external script helper
            const dataLayer = externalScriptHelper.getExternalScript<{ push: (message: any) => void }>('dataLayer');
            if (!dataLayer) {
                console.error('DataLayer script is not available.');

                return;
            }

            const hashedEmail = hashEmailWithCryptoJS(email);
            if (loginMethod === 'Register') {
                // Handle registration event
                dataLayer.push({
                    event: 'sign_up',
                    method: 'basket',
                    hashedEmail: hashedEmail,
                    flow: 'register-and-login', // Indicate the flow
                });
                // Additional user info if needed
            } else if (loginMethod === 'Login') {
                // Handle other login methods
                dataLayer.push({
                    event: 'login',
                    method: 'basket',
                    hashedEmail: hashedEmail,
                });
                // Additional user info if needed
                dataLayer.push({
                    event: 'user_info',
                    hashedEmail: hashedEmail,
                });
            }
        } catch (error) {
            console.error('Error while sending hashed email:', error);
        }
    }

    public emptyEvent(eventName: GtmEmptyEvents): void {
        try {
            this.sendMessage(eventName, {}, undefined, true);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:emptyEvent'));
        }
    }

    public checkoutPageViewStep(location: string, pageCategory: string, pageSubCategory: string, pageType: string = 'checkout'): void {
        try {

            const pageViewData = {
                page: location,
                PageType: pageType,
                testData: 'false',
                pageCategory: pageCategory,
                pageSubCategory: pageSubCategory
            };

            this.sendPageViewMessage(pageViewData);

        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:checkoutPageViewStep'));
        }
    }

    public createLoginProcessEvent(location: string, eventName: string, additionalParams: any = undefined) {
        try {
            const loginEvent = {
                ...additionalParams,
                event: eventName,
                page: location
            };

            this.sendLoginProcessMessage(loginEvent);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'google.repository:checkoutPageViewStep'));
        }
    }

    private createDefaultProductActionData(item: BasketItemModel, quantity: number): any {
        return {
            currency: 'GBP',
            value: item.basePrice.value * quantity,
            items: [{
                item_id: item?.product?.code,
                item_name: item?.product?.brDocumentID,
                affiliation: 'Online',
                coupon: undefined,
                discount: undefined,
                index: 0,
                item_brand: item?.product?.range,
                item_variant: item.product?.swatchColour,
                item_category: item?.product?.bcop,
                item_category2: item?.product.categories[1]?.code,
                item_category3: item?.product?.productOption?.name,
                price: item?.totalPrice?.value / quantity,
                quantity: quantity,
                stock_status: item?.product?.stock?.stockLevelStatus,
            }],
            add: {
                products: [{
                    sku: item?.product?.code
                }]
            }
        };
    }

    private createDefaultProductEventLevelData(item: BasketItemModel, userId?: string): any {
        return {
            productID: item?.product?.brDocumentID,
            productName: `${item?.product?.range} ${item?.product?.bcop}`.toUpperCase(),
            ...(userId && { user_id: userId })
        };
    }

    private createAdditionalProductActionData(
        productType: Exclude<ProductTypes, ProductTypes.DefaultProduct | ProductTypes.Sofaglide>,
        code: string,
        quantity: number
    ): any {
        let productName = '';
        switch (productType) {
            case ProductTypes.Donation:
                productName = 'Pennies';
                break;
            case ProductTypes.FabricCareKit:
                productName = 'Care Kit Fabric';
                break;
            case ProductTypes.LeatherCareKit:
                productName = 'Care Kit Leather';
                break;
            case ProductTypes.Sofashield:
                productName = 'Sofashield Plus';
                break;
            case ProductTypes.SofaRescue:
                productName = 'Sofa Rescue';
                break;
            default:
                break;
        }

        return {
            currency: 'GBP',
            value: 0,
            items: [{
                item_id: code,
                item_name: productName,
                quantity: quantity
            }]
        };
    }

    private createSofaglideProductActionData(
        code: string,
        quantity: number,
        category?: string,
        price?: BasketPriceModel
    ): any {
        return {
            currency: 'GBP',
            value: price ? price.value * quantity : 0,
            items: [{
                item_id: code,
                item_name: 'SOFAGLIDE',
                item_category: category,
                price: price ? price.value : undefined,
                quantity: quantity
            }]
        };
    }

    private mapBasketEntitiesToAnalyticProducts(entities: BasketItemModel[], leadTime?: number): any[] {
        return entities.map((entity, index) => {
            let quantity = entity.quantity;


            if (entity.product.type === ProductTypes.Sofashield) {
                quantity = entities
                    .filter(e => e.product.type === ProductTypes.DefaultProduct)
                    .reduce((sum, e) => sum + (e.sofashieldQuantity || 0), 0);
            }


            const product = {
                item_id: entity?.product?.code,
                item_name: entity?.product?.brDocumentID,
                affiliation: 'Online',
                coupon: undefined,
                discount: undefined,
                index: index,
                item_brand: entity?.product?.range,
                item_variant: entity.product?.swatchColour,
                item_category: entity?.product?.bcop,
                item_category2: entity?.product.categories[1]?.code,
                item_category3: entity?.product?.productOption?.name,
                price: entity?.totalPrice?.value / quantity,
                quantity: quantity,
                stock_status: entity?.product?.stock?.stockLevelStatus,
                lead_time: leadTime?.toString()
            };

            return product;
        });
    }

    private sendMessage(eventName: string, ecommerceActionData: any, additionalParams: any | undefined = undefined, isEmptyEvent: boolean = false, eventLevelData: any | undefined = undefined): void {
        const dataLayer = externalScriptHelper.getExternalScript<{ push: (message: any) => void }>('dataLayer');
        let message: any = {
            event: eventName
        };


        if (!isEmptyEvent) {
            message = {
                event: eventName,
                ...eventLevelData,
                ecommerce: {
                    ...ecommerceActionData,
                    ...additionalParams
                },

            };
        }

        if (!dataLayer) {
            throw new BasketErrorModel(DEFAULT_ERROR_TEXT, 'Google analytics object does not exist in window', 'google.repository:sendMessage');
        }

        dataLayer.push({ ecommerce: null });
        dataLayer.push(message);
    }

    private sendPageViewMessage(pageViewData: any): void {
        const dataLayer = externalScriptHelper.getExternalScript<{ push: (message: any) => void }>('dataLayer');

        let message: any = {
            event: 'pageview',
            ...pageViewData
        };

        if (!dataLayer) {
            throw new BasketErrorModel(DEFAULT_ERROR_TEXT, 'Google analytics object does not exist in window', 'google.repository:sendMessage');
        }

        dataLayer.push(message);
    }

    private sendLoginProcessMessage(eventData: any): void {
        const dataLayer = externalScriptHelper.getExternalScript<{ push: (message: any) => void }>('dataLayer');

        let message: any = {
            PageType: 'login',
            pageCategory: 'login',
            pageSubCategory: 'login',
            method: 'checkout',
            ...eventData
        };

        if (!dataLayer) {
            throw new BasketErrorModel(DEFAULT_ERROR_TEXT, 'Google analytics object does not exist in window', 'google.repository:sendMessage');
        }

        dataLayer.push(message);
    }
}

export const google = new GoogleRepository();